<template>

    <div class="text-sm space-y-2">
        <p>Use one of the provided examples if you’re using a custom checkout integrated into your platform and users must sign up before purchasing.</p>
        <p>To track purchases accurately, you need to notify Partnero when users sign up—before they complete their purchase. You can implement this using either a front-end or back-end integration.</p>
    </div>

    <!-- Partnero Universal -->
    <div class="space-y-4">

        <div class="space-y-2">
            <h3 class="text-md font-semibold">
                Partnero Universal
            </h3>
            <p class="text-sm">
                Partnero Universal provides a universal method to capture sign-ups from forms that use the <badge type="neutral" title="form.submit"></badge> event. This is the simplest no-code solution to capture sign-ups without additional coding. Please note that more advanced forms may require more advanced integration options.            </p>
        </div>

        <div class="relative">
            <code-snippet :code="universalExample" language="javascript" height="h-18" />
            <small class="text-gray-500 mt-1 block">
                Include this <badge type="neutral" title="sign-up tracking snippet"></badge> at the bottom on the page of your website that captures sign-ups. If that is not possible, insert the script provided below directly after the PartneroJS code in the same section.
            </small>
        </div>

    </div>

    <div class="col-span-2 py-3 self-stretch relative">
        <div class="flex items-center justify-center relative">
            <hr class="w-full border border-b-0 absolute top-1/2 transform -translate-y-1/2">
            <span class="text-xs bg-white text-black px-2 py-1 uppercase z-10">OR</span>
        </div>
    </div>

    <div class="space-y-4">

        <div class="space-y-2">
            <h3 class="text-md font-semibold">
                Front-end integration (JavaScript)
            </h3>
            <div class="space-y-1">
                <p class="text-sm">Run this script right after a user signs up to guarantee accurate tracking.</p>
                <p class="text-sm">
                    Please note that while creating a customer you have to replace <b>key</b>, <b>name</b> and <b>email</b> with a real data from your platform.
                </p>
            </div>
        </div>

        <div class="relative">
            <code-snippet :code="javascriptExample" language="javascript" />
            <small class="text-gray-500 mt-1 block">
                Customers referred by partners are automatically identified when their data matches information stored in the <span class="font-semibold" v-if="programType === 'affiliate'">partnero_partner</span> <span class="font-semibold" v-else-if="programType === 'refer_a_friend'">partnero_referral</span> cookie, which is set by our PartneroJS script. This process is handled by the PartneroJS script, ensuring accurate recognition of referred customers.
            </small>
        </div>
    </div>

    <div class="col-span-2 py-3 self-stretch relative">
        <div class="flex items-center justify-center relative">
            <hr class="w-full border border-b-0 absolute top-1/2 transform -translate-y-1/2">
            <span class="text-xs bg-white text-black px-2 py-1 uppercase z-10">OR</span>
        </div>
    </div>

    <div class="space-y-4">

        <div class="space-y-2">
            <h3 class="text-md font-semibold">
                Back-end integration (API)
            </h3>
            <div class="space-y-1" v-if="programType === 'affiliate'">
                <p class="text-sm">Make a request to Partnero API once a customer signed up to your platform.</p>
            </div>
            <div class="space-y-1" v-else-if="programType === 'refer_a_friend'">
                <p class="text-sm">In Refer-A-Friend program each customer of your application can be both referring customer and referred customer.</p>
                <p class="text-sm">Every time your application gets a new signup you need to create a customer profile at Partnero.</p>
            </div>
        </div>

        <div class="relative">
            <textarea id="snippet-2" rows="1" class="bg-gray-50 block w-full text-sm rounded border-gray-200 focus:ring-0 focus:border-gray-400 mb-1 scroll-h font-monospace text-xs">POST https://api.partnero.com/v1/customers</textarea>
        </div>


        <div class="space-y-4" v-if="programType === 'affiliate'">

            <div class="text-sm font-semibold">cURL request example</div>
            <div class="relative">
                <code-snippet :code="curlExample" language="curl" />
            </div>

            <div class="text-sm space-y-1 mb-4">
                <p>
                    Please note that while creating a customer you have to replace <b>key</b>, <b>name</b> and <b>email</b> with a real data from your platform.
                </p>
                <p>
                    Read full API documentation <a class="underline hover:no-underline" href="https://developers.partnero.com/reference/customers.html#create-a-customer" target="_blank">here</a>.
                </p>
            </div>

            <alert type="warning">
                You have to replace <b>partner.key</b> data object with <b>UNIQUE_PARTNER_KEY</b>, which is saved in <b>partnero_partner</b> cookie created by our Partnero Universal script snippet.
            </alert>

        </div>

        <div class="space-y-4" v-if="programType === 'refer_a_friend'">

            <div class="text-sm font-semibold">cURL request example to create a customer</div>
            <div class="relative">
                <code-snippet :code="curlReferralExample" language="curl" />
            </div>

            <div class="text-sm font-semibold">cURL request example to create a referred customer</div>
            <div class="relative">
                <code-snippet :code="curlReferralExampleReferred" language="curl" />
            </div>

            <alert type="warning">
                This type of customer is created when you can find <b>partnero_referral</b> cookie, and the customer is recognized as referred customer.
            </alert>

            <div class="text-sm space-y-1 mb-4">
                <p>
                    Please note that while creating a customers you have to replace <b>key</b>, <b>name</b> and <b>email</b> with a real data from your platform.
                </p>
                <p>
                    Read full API documentation <a class="underline hover:no-underline" href="https://developers.partnero.com/reference/referral-customers.html#create-a-customer" target="_blank">here</a> or you can check Refer-a-Friend integration
                    <a class="underline hover:no-underline" href="https://developers.partnero.com/guide/referral.html#creating-customers" target="_blank">guide</a>.
                </p>
            </div>

        </div>


    </div>
</template>

<script setup>

const props = defineProps({
    programId: {
        type: Number,
        required: true
    },
    config: {
        type: Object,
        default: null
    },
    programType: {
        type: String
    }
});

const universalExample = `<script>
po('integration', 'universal', null);
</scr` + `ipt>`

const javascriptExample = `<script>
po('customers',
  'signup', {
    data: {
        key: 'customer_123456', // or email address
        name: 'John',
        email: 'john.doe@partnero.com'
    }
  }
);
</scr` + `ipt>`

const curlExample = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
"partner": {
"key": "PARTNER_KEY"
},
"key": "CUSTOMER_KEY",
"email": "customer@partnero.com",
"name": "Partnero",
"surname": "Customer"
}'`

const curlReferralExample = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
  "id": "referring_id",
  "key": "referring_key",
  "name": "Customer",
  "email": "customer@partnero.com",
  "metadata": {
    "date_of_birth": "1993-05-19"
  }
}'`

const curlReferralExampleReferred = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
  "id": "referred_id",
  "key": "referred_key",
  "name": "Referred customer",
  "email": "referred-customer@partnero.com",
  "referring_customer": {
    "id": "referring_id",
    "key": "referring_key"
  },
  "metadata": {
    "date_of_birth": "1993-05-19"
  }
}'`

</script>

<style scoped>

</style>
